import { useTranslation } from 'react-i18next'

import { ChoicePlotView, ChoicePlotViewMode, donutAlternative, readQuickStudyChoice } from '@digital-twins/deck-ui/plots/choice-bars'

import { Result } from './quickStudyTypes'

/**
 * A quick-study choice read drawn from Python's `choice_plot` (plots plan J):
 * the canonical `choice-bars` plot, with the bars / donut toggle when Python
 * stamped a share-donut alternative. Chrome in the viewer's UI language
 * (owner decision 5). Returns null when the result carries no plot (stored
 * results, cuts, monadic runs): the caller keeps its browser chart.
 */

export interface QuickStudyChoicePlotProps {
  result: Pick<Result, 'choice_plot'>
  /** `pie` opens on the donut when one was stamped. */
  initialMode?: ChoicePlotViewMode
}

export function QuickStudyChoicePlot(props: QuickStudyChoicePlotProps) {
  const { result, initialMode } = props

  const { t, i18n } = useTranslation('app')

  const read = readQuickStudyChoice(result)
  if (!read) {
    return null
  }
  const donut = donutAlternative(read.alternatives)
  return (
    <ChoicePlotView
      data={read.payload.data}
      donut={donut}
      initialMode={donut ? initialMode : 'choice-bars'}
      highlight={read.payload.props?.highlight}
      toggleLabels={{ bars: t('studyReport.plotToggle.bars'), donut: t('studyReport.plotToggle.donut') }}
      otherToggleLabels={{ expand: (count: number) => t('studyReport.plotOther.expand', { count }), collapse: t('studyReport.plotOther.collapse') }}
      locale={i18n.language}
      testIdPrefix="quick-study-choice-plot"
    />
  )
}

/** Whether a result can draw through `QuickStudyChoicePlot` (a stamped plot is present). */
export function hasQuickStudyChoicePlot(result: Pick<Result, 'choice_plot'>): boolean {
  return readQuickStudyChoice(result) !== null
}
